/**
 * Subscription Management Utility Functions
 *
 * This module provides reusable utility functions for subscription management
 * following DRY principles and best practices for Stripe integration.
 *
 * Key principles:
 * - Single source of truth (Stripe API)
 * - Type safety with TypeScript
 * - Reusable formatting functions
 * - Consistent error handling
 *
 * @see docs/features/STRIPE_SUBSCRIPTIONS.md
 */

import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { getErrorMessage } from "./errorHandling";
import type { SubscriptionStatusType } from "../types/subscription";

export { SUBSCRIPTION_CONSTANTS } from "./subscriptionHelpers.constants";
export {
  formatCurrency,
  formatDate,
  formatPercentage,
  getIntervalText,
  normaliseOrganisationName,
} from "./subscriptionFormatters";
export {
  generateCleanOnboardingUrl,
  generateStripeUrl,
  isPartnerPrice,
} from "./subscriptionUrls";

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "utils/subscriptionHelpers",
);

interface SubscriptionMetricsInput {
  totalSubscriptions: number;
  activeSubscriptions: number;
  trialSubscriptions: number;
  monthlyRevenue: number;
  churnRate: number;
}

interface SubscriptionOperationError {
  status?: number;
  message?: string;
}

/**
 * Copies text to clipboard with error handling
 *
 * @param text - Text to copy to clipboard
 * @returns Promise that resolves to success boolean
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  const clipboard = (globalThis as { navigator?: Navigator }).navigator
    ?.clipboard;
  if (!clipboard) {
    logger.warn("Clipboard API is unavailable", {
      operation: "copy_to_clipboard_unavailable",
    });
    return false;
  }

  try {
    await clipboard.writeText(text);
    return true;
  } catch (error) {
    logger.warn("Failed to copy to clipboard", {
      operation: "copy_to_clipboard_failed",
      errorMessage: getErrorMessage(error),
    });
    return false;
  }
};

/**
 * Calculates subscription metrics for analytics
 *
 * @param statistics - Raw subscription statistics from API
 * @returns Calculated metrics object
 */
export const calculateSubscriptionMetrics = (
  statistics: SubscriptionMetricsInput,
) => {
  const {
    totalSubscriptions,
    activeSubscriptions,
    trialSubscriptions,
    monthlyRevenue,
    churnRate,
  } = statistics;

  return {
    activePercentage:
      totalSubscriptions > 0
        ? (activeSubscriptions / totalSubscriptions) * 100
        : 0,
    arpu: activeSubscriptions > 0 ? monthlyRevenue / activeSubscriptions : 0,
    trialPercentage:
      activeSubscriptions + trialSubscriptions > 0
        ? (trialSubscriptions / (activeSubscriptions + trialSubscriptions)) *
          100
        : 0,
    averageLifetime: churnRate > 0 ? Math.round(1 / (churnRate / 100)) : 0,
  };
};

/**
 * Validates subscription status for UI state management
 *
 * @param status - Subscription status from Stripe
 * @returns Object with boolean flags for different states
 */
export const getSubscriptionStatusFlags = (status: SubscriptionStatusType) => {
  return {
    isActive: status === "active",
    isTrialing: status === "trialing",
    isPastDue: status === "past_due",
    isCanceled: status === "canceled",
    isIncomplete: status === "incomplete",
    isUnpaid: status === "unpaid",
    isPaused: status === "paused",
    requiresAction: ["incomplete", "past_due", "unpaid"].includes(status),
    isHealthy: ["active", "trialing"].includes(status),
  };
};

/**
 * Error handling utility for subscription operations
 *
 * @param error - Error object from API calls
 * @returns User-friendly error message
 */
export const getSubscriptionErrorMessage = (
  error: Error | SubscriptionOperationError | null | undefined,
): string => {
  const status =
    error &&
    typeof error === "object" &&
    "status" in error &&
    typeof error.status === "number"
      ? error.status
      : undefined;

  if (status === 401) {
    return "Authentication failed. Please log in again.";
  }
  if (status === 403) {
    return "You do not have permission to access this subscription data.";
  }
  if (status === 404) {
    return "Subscription data not found.";
  }
  if (typeof status === "number" && status >= 500) {
    return "Server error. Please try again later.";
  }

  const message = getErrorMessage(error);
  return message || "An unexpected error occurred.";
};
